import { infiniteQueryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";
import { toApiError } from "../../../api/problem";

export type StaffAction = components["schemas"]["StaffAction"];
type StaffActionPage = components["schemas"]["StaffActionPage"];

/** The page's filters (users-and-roles UR-43): each one the route's query parameter of the same name. */
export type ActionFilters = {
  user_id?: string;
  device_id?: string;
  org_id?: string;
  firmware_id?: string;
  release_id?: string;
  from?: string; // YYYY-MM-DD, the day's start in the browser's time zone
  to?: string; // YYYY-MM-DD, through the end of that day
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value is a real calendar day as YYYY-MM-DD (not "abc", not 2026-02-31), so it can become an instant. */
export function isDay(v: unknown): v is string {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = [Number(v.slice(0, 4)), Number(v.slice(5, 7)), Number(v.slice(8, 10))];
  const at = new Date(y, m - 1, d);
  return at.getFullYear() === y && at.getMonth() === m - 1 && at.getDate() === d;
}

/** The route's query from the filters: ids only when they are ids, days as instants (from inclusive, to through the
 * end of its day). */
export function actionQuery(f: ActionFilters): Record<string, string> {
  const q: Record<string, string> = {};
  for (const k of ["user_id", "device_id", "org_id", "firmware_id", "release_id"] as const) {
    const v = f[k]?.trim();
    if (v && UUID.test(v)) q[k] = v.toLowerCase();
  }
  if (isDay(f.from)) q.from = new Date(`${f.from}T00:00:00`).toISOString();
  if (isDay(f.to)) {
    const end = new Date(`${f.to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    q.to = new Date(end.getTime() - 1).toISOString();
  }
  return q;
}

/** Whether a filter holds text that is not an id, so the page can say it is ignored. */
export function badId(v: string | undefined): boolean {
  return !!v?.trim() && !UUID.test(v.trim());
}

const ops = () => apiClient("ops");

export const staffActionsQuery = (f: ActionFilters) => {
  const query = actionQuery(f);
  return infiniteQueryOptions({
    queryKey: ["ops", "/v1/ops/actions", query],
    // JSON here; the route also answers CSV (exportActions), hence the union the client types.
    queryFn: async ({ pageParam }) =>
      (await unwrap(
        ops().GET("/v1/ops/actions", {
          params: { query: { ...query, limit: 100, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      )) as StaffActionPage,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
};

/** The same rows as CSV (UR-43), read whole since the download needs the token. */
export async function exportActions(f: ActionFilters): Promise<string> {
  const { data, error, response } = await ops().GET("/v1/ops/actions", {
    params: { query: { ...actionQuery(f), format: "csv" } },
    parseAs: "text",
  });
  if (!response.ok) throw toApiError(response.status, error, response.headers.get("Retry-After"));
  return data as unknown as string;
}

/** The people in the rows read so far, for the person filter: id and e-mail, by e-mail. */
export function peopleIn(items: readonly StaffAction[]): { id: string; email: string }[] {
  const byId = new Map<string, string>();
  for (const a of items) if (!byId.has(a.user_id)) byId.set(a.user_id, a.email ?? a.user_id);
  return [...byId].map(([id, email]) => ({ id, email })).sort((a, b) => a.email.localeCompare(b.email));
}

/** The filters from the page's URL, so a link can open the log filtered (a device's Audit tab, the overview, a release).
 * A day that is not a real YYYY-MM-DD is dropped, so a hand-edited link shows every day instead of crashing. */
export function parseActionFilters(search: Record<string, unknown>): ActionFilters {
  const f: ActionFilters = {};
  for (const k of ["user_id", "device_id", "org_id", "firmware_id", "release_id"] as const) {
    const v = search[k];
    if (typeof v === "string" && v) f[k] = v;
  }
  for (const k of ["from", "to"] as const) if (isDay(search[k])) f[k] = search[k];
  return f;
}
